import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ActualizarUsuarioPayload,
  Auditoria,
  CargaMasivaArchivo,
  CargaMasivaBienDetalle,
  CargaMasivaFacturacionDetalle,
  ClienteMovimiento,
  ClientePerfil,
  ClienteProducto,
  CrearUsuarioPayload,
  DashboardFondo,
  DashboardInversionistas,
  EnvioCredencialesClientesResponse,
  EstadoCuentaClienteFondo,
  EstadoCuentaGenerado,
  GestorBienReporte,
  GestorCliente,
  GestorEmpresa,
  GestorProducto,
  PersonaVerificacionResponse,
  ResumenArrendatariosResponse,
  Usuario
} from './api.models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  usuarios(): Observable<Usuario[]> {
    return this.http.get<Usuario[]>(`${this.apiUrl}/usuarios`);
  }

  crearUsuario(payload: CrearUsuarioPayload): Observable<Usuario> {
    return this.http.post<Usuario>(`${this.apiUrl}/usuarios`, payload);
  }

  actualizarUsuario(id: number, payload: ActualizarUsuarioPayload): Observable<Usuario> {
    return this.http.put<Usuario>(`${this.apiUrl}/usuarios/${id}`, payload);
  }

  eliminarUsuario(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/usuarios/${id}`);
  }

  cambiarEstadoUsuario(id: number, activo: number): Observable<Usuario> {
    return this.http.patch<Usuario>(`${this.apiUrl}/usuarios/${id}/estado`, { activo });
  }

  auditoria(): Observable<Auditoria[]> {
    return this.http.get<Auditoria[]>(`${this.apiUrl}/admin/auditoria`);
  }

  dashboardFondos(): Observable<DashboardFondo[]> {
    return this.http.get<DashboardFondo[]>(`${this.apiUrl}/dashboard/fondos`);
  }

  dashboardInversionistas(codEmpresa: string, codFiducia: string): Observable<DashboardInversionistas> {
    return this.http.get<DashboardInversionistas>(`${this.apiUrl}/dashboard/inversionistas`, {
      params: { codEmpresa, codFiducia }
    });
  }

  gestorProductos(empresa: string): Observable<GestorProducto[]> {
    return this.http.get<GestorProducto[]>(`${this.apiUrl}/clientes/accesos/productos`, {
      params: { empresa }
    });
  }

  gestorClientes(empresa: string, fiducia: string): Observable<GestorCliente[]> {
    return this.http.get<GestorCliente[]>(`${this.apiUrl}/clientes/accesos/gestor`, {
      params: { empresa, fiducia, limite: 500 }
    });
  }

  reporteBienesGestor(codEmpresa: string, codFiducia: string, codBien?: string, fechaCorte?: string): Observable<GestorBienReporte> {
    let params = new HttpParams()
      .set('codEmpresa', codEmpresa)
      .set('codFiducia', codFiducia);
    if (codBien?.trim()) {
      params = params.set('codBien', codBien.trim());
    }
    if (fechaCorte?.trim()) {
      params = params.set('fechaCorte', fechaCorte.trim());
    }
    return this.http.get<GestorBienReporte>(`${this.apiUrl}/reportes/bienes-gestor`, { params });
  }

  reporteBienesGestorPdf(codEmpresa: string, codFiducia: string, codBien?: string, fechaCorte?: string): Observable<Blob> {
    let params = new HttpParams()
      .set('codEmpresa', codEmpresa)
      .set('codFiducia', codFiducia);
    if (codBien?.trim()) {
      params = params.set('codBien', codBien.trim());
    }
    if (fechaCorte?.trim()) {
      params = params.set('fechaCorte', fechaCorte.trim());
    }
    return this.http.get(`${this.apiUrl}/reportes/bienes-gestor/pdf`, {
      params,
      responseType: 'blob'
    });
  }

  reporteBienesGestorExcel(codEmpresa: string, codFiducia: string, codBien?: string, fechaCorte?: string): Observable<Blob> {
    let params = new HttpParams()
      .set('codEmpresa', codEmpresa)
      .set('codFiducia', codFiducia);
    if (codBien?.trim()) {
      params = params.set('codBien', codBien.trim());
    }
    if (fechaCorte?.trim()) {
      params = params.set('fechaCorte', fechaCorte.trim());
    }
    return this.http.get(`${this.apiUrl}/reportes/bienes-gestor/excel`, {
      params,
      responseType: 'blob'
    });
  }

  resumenArrendatarios(codEmpresa: string, codFiducia: string, fechaCorte?: string): Observable<ResumenArrendatariosResponse> {
    let params = new HttpParams()
      .set('codEmpresa', codEmpresa)
      .set('codFiducia', codFiducia);
    if (fechaCorte?.trim()) {
      params = params.set('fechaCorte', fechaCorte.trim());
    }
    return this.http.get<ResumenArrendatariosResponse>(`${this.apiUrl}/reportes/resumen-arrendatarios`, {
      params
    });
  }

  resumenArrendatariosPdf(codEmpresa: string, codFiducia: string, fechaCorte?: string): Observable<Blob> {
    let params = new HttpParams()
      .set('codEmpresa', codEmpresa)
      .set('codFiducia', codFiducia);
    if (fechaCorte?.trim()) {
      params = params.set('fechaCorte', fechaCorte.trim());
    }
    return this.http.get(`${this.apiUrl}/reportes/resumen-arrendatarios/pdf`, {
      params,
      responseType: 'blob'
    });
  }

  resumenArrendatariosExcel(codEmpresa: string, codFiducia: string, fechaCorte?: string): Observable<Blob> {
    let params = new HttpParams()
      .set('codEmpresa', codEmpresa)
      .set('codFiducia', codFiducia);
    if (fechaCorte?.trim()) {
      params = params.set('fechaCorte', fechaCorte.trim());
    }
    return this.http.get(`${this.apiUrl}/reportes/resumen-arrendatarios/excel`, {
      params,
      responseType: 'blob'
    });
  }

  verificarPersona(codEmpresa: string, tipoBusqueda: string, filtro: string): Observable<PersonaVerificacionResponse> {
    return this.http.get<PersonaVerificacionResponse>(`${this.apiUrl}/reportes/verificacion-persona`, {
      params: {
        codEmpresa,
        tipoBusqueda,
        filtro,
        limite: 50
      }
    });
  }

  empresasVerificacionPersona(): Observable<GestorEmpresa[]> {
    return this.http.get<GestorEmpresa[]>(`${this.apiUrl}/reportes/verificacion-persona/empresas`);
  }

  crearAccesoCliente(cliente: GestorCliente): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/clientes/accesos`, {
      gestorCodPersona: cliente.gestorCodPersona,
      identificacion: cliente.identificacion
    });
  }

  enviarCredencialesCliente(cliente: GestorCliente): Observable<EnvioCredencialesClientesResponse> {
    return this.http.post<EnvioCredencialesClientesResponse>(
      `${this.apiUrl}/clientes/accesos/${cliente.gestorCodPersona}/credenciales`,
      {}
    );
  }

  enviarCredencialesClientes(gestorCodPersonas: number[]): Observable<EnvioCredencialesClientesResponse> {
    return this.http.post<EnvioCredencialesClientesResponse>(`${this.apiUrl}/clientes/accesos/credenciales`, {
      gestorCodPersonas
    });
  }

  estadoCuentaClientes(codProducto: string): Observable<EstadoCuentaClienteFondo[]> {
    return this.http.get<EstadoCuentaClienteFondo[]>(`${this.apiUrl}/admin/estados-cuenta/clientes`, {
      params: { codProducto }
    });
  }

  generarEstadosCuenta(
    codProducto: string,
    fechaDesde: string,
    fechaHasta: string,
    clientes?: { codCliente: number; numCuenta: string }[],
    regenerar = false
  ): Observable<EstadoCuentaGenerado[]> {
    return this.http.post<EstadoCuentaGenerado[]>(`${this.apiUrl}/admin/estados-cuenta/generar`, {
      codProducto,
      fechaDesde,
      fechaHasta,
      clientes,
      regenerar
    });
  }

  estadosCuentaGenerados(codProducto: string, fechaDesde: string, fechaHasta: string): Observable<EstadoCuentaGenerado[]> {
    return this.http.get<EstadoCuentaGenerado[]>(`${this.apiUrl}/admin/estados-cuenta`, {
      params: { codProducto, fechaDesde, fechaHasta }
    });
  }

  estadoCuentaGeneradoPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/admin/estados-cuenta/${id}/pdf`, {
      responseType: 'blob'
    });
  }

  enviarEstadoCuenta(id: number): Observable<EstadoCuentaGenerado> {
    return this.http.post<EstadoCuentaGenerado>(`${this.apiUrl}/admin/estados-cuenta/${id}/enviar`, {});
  }

  enviarEstadosCuenta(ids: number[]): Observable<EstadoCuentaGenerado[]> {
    return this.http.post<EstadoCuentaGenerado[]>(`${this.apiUrl}/admin/estados-cuenta/enviar-masivo`, { ids });
  }

  clientePerfil(): Observable<ClientePerfil> {
    return this.http.get<ClientePerfil>(`${this.apiUrl}/cliente/perfil`);
  }

  clienteProductos(): Observable<ClienteProducto[]> {
    return this.http.get<ClienteProducto[]>(`${this.apiUrl}/cliente/productos`);
  }

  descargarEstadoCuentaPdf(producto: ClienteProducto, fechaDesde: string, fechaHasta: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/cliente/estado-cuenta/pdf`, {
      params: this.cuentaParams(producto, fechaDesde, fechaHasta),
      responseType: 'blob'
    });
  }

  clienteEstadosCuentaGenerados(producto: ClienteProducto): Observable<EstadoCuentaGenerado[]> {
    return this.http.get<EstadoCuentaGenerado[]>(`${this.apiUrl}/cliente/estado-cuenta/generados`, {
      params: {
        empresa: producto.empresa,
        fiducia: producto.fiducia,
        numCuenta: producto.numCuenta
      }
    });
  }

  descargarEstadoCuentaGeneradoPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/cliente/estado-cuenta/generados/${id}/pdf`, {
      responseType: 'blob'
    });
  }

  descargarMovimientosExcel(producto: ClienteProducto, fechaDesde: string, fechaHasta: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/cliente/movimientos/excel`, {
      params: this.cuentaParams(producto, fechaDesde, fechaHasta),
      responseType: 'blob'
    });
  }

  clienteMovimientos(producto: ClienteProducto, fechaDesde: string, fechaHasta: string): Observable<ClienteMovimiento[]> {
    return this.http.get<ClienteMovimiento[]>(`${this.apiUrl}/cliente/movimientos`, {
      params: this.cuentaParams(producto, fechaDesde, fechaHasta)
    });
  }

  cargarBienArrendadoExcel(file: File): Observable<CargaMasivaArchivo> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<CargaMasivaArchivo>(`${this.apiUrl}/cargas-masivas/bienes-arrendados/archivo`, formData);
  }

  listarCargasBienArrendado(): Observable<CargaMasivaArchivo[]> {
    return this.http.get<CargaMasivaArchivo[]>(`${this.apiUrl}/cargas-masivas/bienes-arrendados`);
  }

  detallesCargaBienArrendado(id: number): Observable<CargaMasivaBienDetalle[]> {
    return this.http.get<CargaMasivaBienDetalle[]>(`${this.apiUrl}/cargas-masivas/bienes-arrendados/${id}/detalles`);
  }

  procesarCargaBienArrendado(id: number): Observable<CargaMasivaArchivo> {
    return this.http.post<CargaMasivaArchivo>(`${this.apiUrl}/cargas-masivas/bienes-arrendados/${id}/procesar`, {});
  }

  borrarCargaBienArrendado(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/cargas-masivas/bienes-arrendados/${id}`);
  }

  cargarBienFacturacionArchivo(file: File): Observable<CargaMasivaArchivo> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<CargaMasivaArchivo>(`${this.apiUrl}/cargas-masivas/bienes-facturacion/archivo`, formData);
  }

  listarCargasBienFacturacion(): Observable<CargaMasivaArchivo[]> {
    return this.http.get<CargaMasivaArchivo[]>(`${this.apiUrl}/cargas-masivas/bienes-facturacion`);
  }

  detallesCargaBienFacturacion(id: number): Observable<CargaMasivaFacturacionDetalle[]> {
    return this.http.get<CargaMasivaFacturacionDetalle[]>(`${this.apiUrl}/cargas-masivas/bienes-facturacion/${id}/detalles`);
  }

  procesarCargaBienFacturacion(id: number): Observable<CargaMasivaArchivo> {
    return this.http.post<CargaMasivaArchivo>(`${this.apiUrl}/cargas-masivas/bienes-facturacion/${id}/procesar`, {});
  }

  borrarCargaBienFacturacion(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/cargas-masivas/bienes-facturacion/${id}`);
  }

  private cuentaParams(producto: ClienteProducto, fechaDesde: string, fechaHasta: string): HttpParams {
    return new HttpParams()
      .set('empresa', producto.empresa)
      .set('fiducia', producto.fiducia)
      .set('numCuenta', producto.numCuenta)
      .set('fechaDesde', fechaDesde)
      .set('fechaHasta', fechaHasta);
  }
}
